/**
 * Klagenfurt P0.4d — module-level scroll-snapshot registry.
 *
 * Bridges the DealComparables chat widget (rendered inside the
 * advisor conversation) with the AIAdvisorPanel that hosts the
 * scrollable container. When the widget navigates to a comparable
 * internally, it needs the CURRENT ``conversationRef.scrollTop`` +
 * the ULID of the topmost visible advisor turn to persist an exact
 * restore anchor.
 *
 * Multi-registrant contract: TWO AIAdvisorPanel instances share the
 * same ``listingUlid`` on desktop — the always-mounted sidebar panel
 * AND the ``AIAdvisorMobileFab`` portal-mounted variant (permanently
 * mounted even when `display:none`). We store a Set of snapshotters
 * per listing; on capture we return the FIRST snapshotter that
 * yields a non-null result (the visible panel skips the return via
 * its own visibility gate). This prevents an off-screen panel from
 * stomping the visible one in a single-entry Map.
 *
 * Only one panel is ever mounted per listing on mobile viewports,
 * so this generalisation is essentially free.
 */

const registry = new Map();

export function registerAdvisorSnapshotter(listingUlid, fn) {
  if (!listingUlid || typeof fn !== "function") return () => {};
  let set = registry.get(listingUlid);
  if (!set) {
    set = new Set();
    registry.set(listingUlid, set);
  }
  set.add(fn);
  return () => {
    const s = registry.get(listingUlid);
    if (!s) return;
    s.delete(fn);
    if (s.size === 0) registry.delete(listingUlid);
  };
}

export function captureAdvisorSnapshot(listingUlid) {
  if (!listingUlid) return null;
  const set = registry.get(listingUlid);
  if (!set || set.size === 0) return null;
  for (const fn of set) {
    try {
      const res = fn();
      // First non-null result wins. A visible panel returns a real
      // snapshot; an off-screen panel returns null (its own
      // visibility gate).
      if (res) return res;
    } catch {
      /* keep iterating — one broken snapshotter must never block
         a healthy one. */
    }
  }
  return null;
}

// Testing helper — clears the registry.
export function _resetAdvisorSnapshotRegistry() {
  registry.clear();
}
